/**
 * TroiaEmailListItem - Item da lista de emails (Design System "next-screens")
 * Avatar squircle slate + 3 linhas (remetente/hora · assunto · preview)
 * + dot de não-lido/rascunho à esquerda. Estados hover/selecionado via tokens de tema.
 */

import React, { useState } from 'react';
import type { ConversationResponse } from '@troia-v3/types';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import { Icon } from '@/shared/icons'
interface TroiaEmailListItemProps {
  conversation: ConversationResponse;
  isSelected: boolean;
  expanded: boolean;
  onClick: () => void;
}

export const TroiaEmailListItem: React.FC<TroiaEmailListItemProps> = ({
  conversation,
  isSelected,
  expanded,
  onClick,
}) => {
  const [hovered, setHovered] = useState(false);
  const { state: authState } = useAuth();

  // Não-lido é POR USUÁRIO: indexa a própria chave, igual ao chat
  // (ConversationItem.tsx). Somar todas as chaves só funcionava porque o
  // backend já filtra o mapa para o usuário logado — bastava mudar isso lá pra
  // esta tela passar a mostrar o não-lido dos outros.
  const unreadCount = conversation.unreadTracking && authState.user?.id
    ? (conversation.unreadTracking[authState.user.id]?.count || 0)
    : 0;
  const isUnread = unreadCount > 0;

  const isDraft = conversation.tags?.includes('draft') || false;
  const isStarred = conversation.tags?.includes('starred') || false;
  const senderName = conversation.contact?.name || 'Sem remetente';
  const senderAvatar = conversation.contact?.picture;
  const subject = conversation.subject || '(Sem assunto)';
  const preview = conversation.lastMessage || '';
  const date = conversation.lastMessageAt || conversation.createdAt;

  // Indicador de não-lido/rascunho (dot à esquerda, estilo DS)
  const showDot = isUnread || isDraft;
  const dotColor = isDraft
    ? 'var(--theme-warning-color)'
    : 'var(--theme-button-primary)';

  const rowBackground = isSelected
    ? 'var(--theme-surface-selected)'
    : hovered
      ? 'var(--theme-button-ghost-hover)'
      : 'transparent';

  const formatDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    const diff = now.getTime() - d.getTime();
    if (diff < 7 * 24 * 60 * 60 * 1000) {
      return d.toLocaleDateString('pt-BR', { weekday: 'short' });
    }
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  };

  const initials = senderName
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  // Collapsed mode: only avatar + unread badge
  if (!expanded) {
    return (
      <div
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        data-testid={`email-list-item-${conversation.id}`}
        className="relative flex items-center justify-center py-2 px-1 cursor-pointer"
        style={{ backgroundColor: rowBackground }}
      >
        <Avatar className="w-[38px] h-[38px]" isSquircle>
          {senderAvatar ? (
            <AvatarImage src={senderAvatar} alt={senderName} isSquircle />
          ) : (
            <AvatarFallback isSquircle className="text-xs font-bold">
              {initials}
            </AvatarFallback>
          )}
        </Avatar>
        {isUnread && unreadCount > 0 && (
          <span
            className="absolute top-1 right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full"
            style={{
              backgroundColor: 'var(--theme-button-primary)',
              color: 'var(--theme-button-primary-text)',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`email-list-item-${conversation.id}`}
      className="relative flex items-start gap-3 cursor-pointer pl-4 pr-3.5 py-3 border-b"
      style={{
        borderBottomColor: 'var(--theme-card-border)',
        backgroundColor: rowBackground,
      }}
    >
      {/* Marca de não-lido / rascunho: faixa lateral de altura cheia.
          Substituiu um ponto de 7px que, ao lado de uma diferença de peso de
          fonte de 700 vs 600, deixava lido e não-lido praticamente idênticos. */}
      {showDot && (
        <span
          data-testid={`email-unread-mark-${conversation.id}`}
          className="absolute left-0 top-0 bottom-0 w-[3px]"
          style={{ backgroundColor: dotColor }}
        />
      )}

      {/* Avatar squircle */}
      <Avatar className="w-10 h-10 flex-shrink-0" isSquircle>
        {senderAvatar ? (
          <AvatarImage src={senderAvatar} alt={senderName} isSquircle />
        ) : (
          <AvatarFallback isSquircle className="text-sm font-bold">
            {initials}
          </AvatarFallback>
        )}
      </Avatar>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Line 1: sender + star + date */}
        <div className="flex items-center gap-2">
          <span
            data-testid={`email-sender-${conversation.id}`}
            className="text-sm truncate flex-1"
            style={{
              // O que separa lido de não-lido aqui é o CONTRASTE, não o peso:
              // 700 vs 600 é imperceptível, texto cheio vs apagado se lê de longe.
              color: isUnread ? 'var(--theme-text)' : 'var(--theme-muted-text)',
              fontWeight: isUnread ? 700 : 400,
            }}
          >
            {senderName}
          </span>
          {isStarred && (
            <Icon name="star" className="h-3.5 w-3.5 flex-shrink-0"
              style={{ fill: 'var(--theme-warning-color)', color: 'var(--theme-warning-color)' }} />
          )}
          <span
            className="text-[11px] flex-shrink-0"
            style={{
              color: isUnread ? 'var(--theme-text)' : 'var(--theme-muted-text)',
              fontWeight: isUnread ? 600 : 400,
            }}
          >
            {date ? formatDate(date) : ''}
          </span>
        </div>

        {/* Line 2: subject + unread badge */}
        <div className="flex items-center gap-2 mt-0.5">
          <p
            data-testid={`email-subject-${conversation.id}`}
            className="text-[13px] truncate flex-1"
            style={{
              color: isUnread ? 'var(--theme-text)' : 'var(--theme-muted-text)',
              fontWeight: isUnread ? 600 : 400,
            }}
          >
            {subject}
          </p>
          {isUnread && unreadCount > 0 && (
            <span
              className="flex-shrink-0 flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-semibold rounded-full"
              style={{
                backgroundColor: 'var(--theme-button-primary)',
                color: 'var(--theme-button-primary-text)',
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>

        {/* Line 3: preview */}
        {preview && (
          <p
            className="text-xs truncate mt-0.5"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            {preview.replace(/<[^>]*>/g, '').substring(0, 80)}
          </p>
        )}
      </div>
    </div>
  );
};

TroiaEmailListItem.displayName = 'TroiaEmailListItem';
